import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, RefreshCw, LogIn, UserPlus, UserMinus, Pencil, KeyRound, Send, Trash2, Activity } from 'lucide-react';
import { audit } from '../../lib/api';

const when = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };

const actionMeta = (action) => {
  if (action === 'login') return { icon: LogIn, color: 'bg-blue-100 text-blue-700', label: 'Login' };
  if (action === 'user.create') return { icon: UserPlus, color: 'bg-emerald-100 text-emerald-700', label: 'User created' };
  if (action === 'user.delete') return { icon: UserMinus, color: 'bg-red-100 text-red-700', label: 'User removed' };
  if (action === 'user.update') return { icon: Pencil, color: 'bg-amber-100 text-amber-700', label: 'User updated' };
  if (action === 'user.password_reset') return { icon: KeyRound, color: 'bg-amber-100 text-amber-700', label: 'Password reset' };
  if (action === 'invoice.send' || action === 'invoice.send_bulk') return { icon: Send, color: 'bg-purple-100 text-purple-700', label: 'Invoice sent' };
  if (action.endsWith('.delete')) return { icon: Trash2, color: 'bg-red-100 text-red-700', label: action };
  return { icon: Activity, color: 'bg-slate-100 text-slate-700', label: action };
};

const FILTERS = [
  { v: 'all', l: 'All activity' },
  { v: 'login', l: 'Logins' },
  { v: 'user.create', l: 'User created' },
  { v: 'user.delete', l: 'User removed' },
  { v: 'user.update', l: 'User updated' },
  { v: 'invoice.send', l: 'Invoice sent' },
];

export default function AuditLog() {
  const [rows, setRows] = useState(null);
  const [sum, setSum] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    setRows(null);
    audit.list(filter === 'all' ? {} : { action: filter }).then(setRows).catch(() => setRows([]));
    audit.summary().then(setSum).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="audit-log">
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 border-slate-200"><div className="text-2xl font-extrabold text-slate-900">{sum?.total ?? '—'}</div><div className="text-xs text-slate-500">Total events</div></Card>
        <Card className="p-4 border-slate-200"><div className="text-2xl font-extrabold text-slate-900">{sum?.logins ?? '—'}</div><div className="text-xs text-slate-500">Logins</div></Card>
        <Card className="p-4 border-slate-200"><div className="text-2xl font-extrabold text-[#6FA82C]">{sum?.last24h ?? '—'}</div><div className="text-xs text-slate-500">Last 24 hours</div></Card>
      </div>

      <div className="flex items-center justify-between gap-2">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 h-9" data-testid="audit-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{FILTERS.map(f => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="audit-refresh"><RefreshCw className="w-4 h-4" />Refresh</Button>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        {rows === null ? (
          <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-slate-500">No activity recorded yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
              <tr><th className="px-4 py-2.5 text-left">Action</th><th className="px-4 py-2.5 text-left">Who</th><th className="px-4 py-2.5 text-left">Details</th><th className="px-4 py-2.5 text-left">When</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(r => {
                const m = actionMeta(r.action);
                return (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid="audit-row">
                    <td className="px-4 py-3"><Badge className={`${m.color} border-0 gap-1`}><m.icon className="w-3 h-3" />{m.label}</Badge></td>
                    <td className="px-4 py-3"><span className="font-medium text-slate-900">{r.actor}</span>{r.role && <span className="ml-1 text-xs text-slate-400">({r.role})</span>}</td>
                    <td className="px-4 py-3 text-slate-600">{r.detail || r.target || '—'}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{when(r.at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
